import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Calendar, UserCheck, UserX, Coffee,
  ArrowRight, Filter, Download, Laptop, MapPin,
  AlertTriangle, CheckCircle2, Clock3
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { attendanceData, employeeData } from '../data/mockData';

const AttendanceCard = ({ title, value, total, icon: Icon, color }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-sm"
  >
    <div className="flex justify-between items-start mb-4">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h3 className="text-2xl font-bold text-gray-800 mt-1">{value}</h3>
        {total && (
          <p className="text-sm text-gray-500 mt-1">
            of {total} employees
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={`h-full ${color.replace('bg-', 'bg-')}`}
        style={{ width: `${(value / total) * 100}%` }}
      />
    </div>
  </motion.div>
);

const AttendanceModule = ({ isMobile }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('today'); // today, week, month

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

  const todayStats = [
    {
      title: 'Present',
      value: attendanceData.today.present,
      total: employeeData.stats.totalEmployees,
      icon: UserCheck,
      color: 'bg-green-600'
    },
    {
      title: 'Absent',
      value: attendanceData.today.absent,
      total: employeeData.stats.totalEmployees,
      icon: UserX,
      color: 'bg-red-600'
    },
    {
      title: 'Late',
      value: attendanceData.today.late,
      total: employeeData.stats.totalEmployees,
      icon: Clock3,
      color: 'bg-amber-600'
    },
    {
      title: 'Working Remote',
      value: attendanceData.today.workingRemote,
      total: employeeData.stats.totalEmployees,
      icon: Laptop,
      color: 'bg-indigo-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filter
          </button>
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {todayStats.map((stat, index) => (
          <AttendanceCard key={index} {...stat} />
        ))}
      </div>

      {/* Attendance Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold mb-6">Attendance Trends</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={attendanceData.trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="present" 
                  stroke="#10B981" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="absent" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="late" 
                  stroke="#F59E0B" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Live Attendance Feed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold mb-6">Live Attendance Feed</h3>
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center
                  ${index % 3 === 0 ? 'bg-green-100' : 
                    index % 3 === 1 ? 'bg-amber-100' : 'bg-red-100'}`}
                >
                  {index % 3 === 0 ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : index % 3 === 1 ? (
                    <Clock3 className="w-4 h-4 text-amber-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {['Ahmed Hassan', 'Fatima Abbas', 'Mohammed Ali', 'Sara Khan', 'Omar Yusuf'][index]}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <MapPin className="w-3 h-3" />
                    {['Main Office', 'Working Remote', 'Branch Office'][index % 3]}
                    <span className="text-gray-300">•</span>
                    {['08:58 AM', '09:15 AM', 'Absent'][index % 3]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AttendanceModule; 